---
id: apps-app-react-doc
title: App React
owner: Luc Lemerez
status: Active
lastReviewed: 2026-01-27
visibility: public
tags:
  - app
  - app-react
  - frontend
---

## Product Overview

Consumer web app for `app.rezi.ai`, covering authentication, the primary user
dashboard, and shareable or public-facing entry points.

### Primary Workflow
Users sign up or log in, then move into the authenticated dashboard experience
to manage resumes and related workflows. Shared links and public flows are
supported when users access share routes or anonymous resignation letter entry
points.

### Business Logic
The app splits public routes from protected dashboard routes and uses route
guards to control access. RMS configuration influences theming and localized
content, and certain public flows are available without an authenticated
session.

### Outcomes
The app provides the main consumer experience for onboarding, accessing
dashboard workflows, and sharing content from Rezi.

### Out of Scope
Enterprise-facing experiences are handled by `apps/app-enterprise`, and backend
services are not owned by this frontend app.

## Technical Overview

The app bootstraps in `apps/app-react/src/index.js`, which initializes i18n,
react-query, session timeout handling, and the root store provider before
rendering the main `App` from `apps/app-react/src/rezi-app/app.layout.tsx`.

### Entry Points
`apps/app-react/src/rezi-app/app.layout.tsx` routes between the frontend
consumer experience and the backend download flow based on the URL prefix. The
frontend routes are defined in `apps/app-react/src/rezi-app/front.layout.tsx`,
while the backend download route is defined in
`apps/app-react/src/rezi-app/be.layout.tsx`.

### Key Modules
Authentication, dashboard, and shared flows are wired in
`apps/app-react/src/rezi-app/front.layout.tsx` using `PublicRoute` and
`ProtectedRoute`. The dashboard surface is implemented under
`apps/app-react/src/rezi-app/dashboard`, while share experiences are under
`apps/app-react/src/rezi-app/share`.

### Data and Services
State and session data flow through the root store provider in
`@rezi/stores/providers/root-store-provider`, with react-query configured in
`apps/app-react/src/index.js` for client-side caching. Localization is powered
by i18next and the HTTP backend initialized in the same entrypoint.

### Integrations and Side Effects
The frontend layout initializes RMS configuration, analytics, and support
services such as Intercom, Amplitude, and Clarity via hooks and utilities
defined in `apps/app-react/src/rezi-app/front.layout.tsx` and `src/hoc/JsInsert`.
Query parameters set cookies that influence onboarding and redirect behavior.

### Constraints and Risks
Rendering is gated on user initialization in the root store, and route access
relies on `PublicRoute`/`ProtectedRoute` checks, so regressions there can block
navigation across the app.


## Changelog

- 2026-01-27: Expanded product and technical overviews to cover business logic and key code paths.
- 2026-01-23: Initial documentation.
